import { Accordion, Box, Flex, Grid, Icon, Input, NativeSelect, Span, Text } from '@chakra-ui/react';
import { Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InputGroup } from '@/components/ui/input-group';
import { FilterClassWithOptions, FilterOption, NormalizedColumn, ResponsiveColDef, ResponsiveGridState } from './types';
import { IFilterComp, SortModelItem } from '@ag-grid-community/core';

/**
 * Controlled input that maintains local state for immediate responsiveness
 * while syncing from the external value (source of truth) and debouncing
 * onChange calls back to the parent.
 */
function DebouncedInput({
    value: externalValue,
    onChange,
    debounceMs = 300,
    ...inputProps
}: {
    value: string;
    onChange: (value: string) => void;
    debounceMs?: number;
} & Omit<React.ComponentProps<typeof Input>, 'value' | 'onChange'>) {
    const [localValue, setLocalValue] = useState(externalValue);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();

    // Sync from external value when it changes (e.g. programmatic setFilterModel)
    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        setLocalValue(externalValue);
    }, [externalValue]);

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            setLocalValue(val);
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(() => {
                onChange(val);
            }, debounceMs);
        },
        [onChange, debounceMs],
    );

    return <Input {...inputProps} value={localValue} onChange={handleChange} />;
}

function resolveFilterOptions(filterClass: new () => IFilterComp, filterParams?: Record<string, unknown>): FilterOption[] | null {
    const cls = filterClass as unknown as Partial<FilterClassWithOptions>;
    if (typeof cls.getFilterOptions === 'function') {
        return cls.getFilterOptions(filterParams);
    }
    return null;
}

type CustomFilterHostProps = {
    filterClass: new () => IFilterComp;
    filterParams: Record<string, unknown> | undefined;
    colId: string;
    headerName: string;
    currentModel: unknown;
    onFilterChange: (colId: string, model: unknown) => void;
};

function CustomFilterHost({ filterClass, filterParams, colId, headerName, currentModel, onFilterChange }: CustomFilterHostProps) {
    const extractedOptions = useMemo<FilterOption[] | null>(
        () => resolveFilterOptions(filterClass, filterParams),
        [filterClass, filterParams],
    );

    const currentValue = useMemo(() => {
        if (!currentModel || typeof currentModel !== 'object') {
            return '';
        }
        const rec = currentModel as Record<string, unknown>;
        if ('value' in rec && rec.value !== undefined && rec.value !== null) {
            return String(rec.value);
        }
        return '';
    }, [currentModel]);

    const handleChange = useCallback(
        (value: string) => {
            if (!value) {
                onFilterChange(colId, undefined);
            } else {
                onFilterChange(colId, { value });
            }
        },
        [colId, onFilterChange],
    );

    if (extractedOptions) {
        return (
            <Box>
                <Text fontSize="xs" color="fg.muted" mb="2px">
                    {headerName}
                </Text>
                <NativeSelect.Root size="sm">
                    <NativeSelect.Field value={currentValue} onChange={(e) => handleChange(e.target.value)}>
                        {extractedOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                </NativeSelect.Root>
            </Box>
        );
    }

    return (
        <Box>
            <Text fontSize="xs" color="fg.muted" mb="2px">
                {headerName}
            </Text>
            <Input
                type="search"
                size="sm"
                placeholder={`Filter ${headerName}`}
                value={currentValue}
                onChange={(e) => handleChange((e.target as HTMLInputElement).value)}
            />
        </Box>
    );
}

type CardFilterBarProps<TData, TColId extends string> = {
    columns: NormalizedColumn<TData, TColId>[];
    state: ResponsiveGridState;
    onSortChange: (sortModel: SortModelItem[]) => void;
    onFilterChange: (colId: string, value: unknown) => void;
};

function getFilterValue(filterModel: Record<string, unknown>, colId: string): string {
    const entry = filterModel[colId];
    if (!entry || typeof entry !== 'object') {
        return '';
    }
    const rec = entry as Record<string, unknown>;
    // AG Grid filter model shapes: { filter: ... }, { dateFrom: ... }, { value: ... }
    if ('filter' in rec && rec.filter !== undefined && rec.filter !== null) {
        return String(rec.filter);
    }
    if ('dateFrom' in rec && rec.dateFrom !== undefined && rec.dateFrom !== null) {
        return String(rec.dateFrom);
    }
    if ('value' in rec && rec.value !== undefined && rec.value !== null) {
        return String(rec.value);
    }
    return '';
}

/**
 * Formats a raw filter value for display in the summary.
 * Looks up the display label from mobile.filterOptions or custom filter class options.
 */
function formatFilterDisplayValue<TData, TColId extends string>(col: NormalizedColumn<TData, TColId>, rawValue: string): string {
    // 1. Look up label from mobile.filterOptions
    const mobileOptions = col.colDef.mobile?.filterOptions;
    if (mobileOptions) {
        const match = mobileOptions.find((opt) => opt.value === rawValue);
        if (match) {
            return match.label;
        }
    }

    if (col.filterType === 'custom' && typeof col.colDef.filter === 'function') {
        const options = resolveFilterOptions(
            col.colDef.filter as unknown as new () => IFilterComp,
            col.colDef.filterParams as Record<string, unknown> | undefined,
        );
        if (options) {
            const match = options.find((opt) => opt.value === rawValue);
            if (match) {
                return match.label;
            }
        }
    }

    return rawValue;
}

export function CardFilterBar<TData, TColId extends string>({
    columns,
    state,
    onSortChange,
    onFilterChange,
}: CardFilterBarProps<TData, TColId>) {
    const filterableColumns = useMemo(() => columns.filter((col) => col.filterable && !col.hidden), [columns]);
    const sortableColumns = useMemo(() => columns.filter((col) => col.sortable && !col.hidden), [columns]);

    const primarySearchCol = useMemo(() => {
        const titleCol = filterableColumns.find((col) => col.emphasis === 'title');
        if (titleCol) {
            return titleCol;
        }
        return filterableColumns.find((col) => col.filterType === 'agTextColumnFilter' || col.filterType === true);
    }, [filterableColumns]);

    const secondaryFilters = useMemo(
        () => filterableColumns.filter((col) => col !== primarySearchCol),
        [filterableColumns, primarySearchCol],
    );

    const handleDebouncedFilterChange = useCallback(
        (colId: string, value: string, filterShape: 'filter' | 'dateFrom' | 'value', parseNumber?: boolean) => {
            if (!value) {
                onFilterChange(colId, undefined);
            } else {
                const parsedValue = parseNumber ? Number(value) : value;
                if (parseNumber && isNaN(parsedValue as number)) {
                    onFilterChange(colId, undefined);
                } else {
                    onFilterChange(colId, { [filterShape]: parsedValue });
                }
            }
        },
        [onFilterChange],
    );

    const handleSelectFilterChange = useCallback(
        (colId: string, value: string, filterShape: 'filter' | 'value') => {
            if (!value) {
                onFilterChange(colId, undefined);
            } else {
                onFilterChange(colId, { [filterShape]: value });
            }
        },
        [onFilterChange],
    );

    const currentSortValue = useMemo(() => {
        if (state.sortModel.length === 0) {
            return '';
        }
        const { colId, sort } = state.sortModel[0];
        return `${colId}:${sort}`;
    }, [state.sortModel]);

    const handleSortChange = useCallback(
        (value: string) => {
            if (!value) {
                onSortChange([]);
                return;
            }
            const [colId, sort] = value.split(':');
            onSortChange([{ colId, sort: sort as 'asc' | 'desc' }]);
        },
        [onSortChange],
    );

    const renderFilterControl = useCallback(
        (col: NormalizedColumn<TData, TColId>) => {
            const currentValue = getFilterValue(state.filterModel, col.colId);

            if (col.colDef.mobile?.filterOptions) {
                return (
                    <Box key={col.colId}>
                        <Text fontSize="xs" color="fg.muted" mb="2px">
                            {col.headerName}
                        </Text>
                        <NativeSelect.Root size="sm">
                            <NativeSelect.Field
                                value={currentValue}
                                onChange={(e) => handleSelectFilterChange(col.colId, e.target.value, 'value')}>
                                <option value="">All</option>
                                {col.colDef.mobile.filterOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                        </NativeSelect.Root>
                    </Box>
                );
            }

            // Custom filterComponent
            if (col.colDef.mobile?.filterComponent) {
                const FilterComponent = col.colDef.mobile.filterComponent;
                return (
                    <Box key={col.colId}>
                        <FilterComponent
                            colId={col.colId}
                            headerName={col.headerName}
                            value={state.filterModel[col.colId]}
                            onChange={(value) => onFilterChange(col.colId, value)}
                            colDef={col.colDef as ResponsiveColDef<TData>}
                        />
                    </Box>
                );
            }

            switch (col.filterType) {
                case 'agTextColumnFilter':
                case true:
                    return (
                        <Box key={col.colId}>
                            <Text fontSize="xs" color="fg.muted" mb="2px">
                                {col.headerName}
                            </Text>
                            <DebouncedInput
                                type="search"
                                size="sm"
                                placeholder={`Filter ${col.headerName}`}
                                value={currentValue}
                                onChange={(val) => handleDebouncedFilterChange(col.colId, val, 'filter')}
                            />
                        </Box>
                    );

                case 'agNumberColumnFilter':
                    return (
                        <Box key={col.colId}>
                            <Text fontSize="xs" color="fg.muted" mb="2px">
                                {col.headerName}
                            </Text>
                            <DebouncedInput
                                type="number"
                                size="sm"
                                placeholder={col.headerName}
                                value={currentValue}
                                onChange={(val) => handleDebouncedFilterChange(col.colId, val, 'filter', true)}
                            />
                        </Box>
                    );

                case 'agDateColumnFilter':
                    return (
                        <Box key={col.colId}>
                            <Text fontSize="xs" color="fg.muted" mb="2px">
                                {col.headerName}
                            </Text>
                            <Input
                                type="date"
                                size="sm"
                                value={currentValue}
                                onChange={(e) => {
                                    const val = (e.target as HTMLInputElement).value;
                                    onFilterChange(col.colId, val ? { dateFrom: val } : undefined);
                                }}
                            />
                        </Box>
                    );

                default:
                    if (col.filterType === 'custom' && typeof col.colDef.filter === 'function') {
                        return (
                            <CustomFilterHost
                                key={col.colId}
                                filterClass={col.colDef.filter as unknown as new () => IFilterComp}
                                filterParams={col.colDef.filterParams as Record<string, unknown> | undefined}
                                colId={col.colId}
                                headerName={col.headerName}
                                currentModel={state.filterModel[col.colId]}
                                onFilterChange={onFilterChange}
                            />
                        );
                    }
                    if (col.filterable) {
                        return (
                            <Box key={col.colId}>
                                <Text fontSize="xs" color="fg.muted" mb="2px">
                                    {col.headerName}
                                </Text>
                                <DebouncedInput
                                    type="search"
                                    size="sm"
                                    placeholder={`Filter ${col.headerName}`}
                                    value={currentValue}
                                    onChange={(val) => handleDebouncedFilterChange(col.colId, val, 'value')}
                                />
                            </Box>
                        );
                    }
                    return null;
            }
        },
        [state.filterModel, handleDebouncedFilterChange, handleSelectFilterChange, onFilterChange],
    );

    // Build a summary of active filters and sort for the accordion trigger
    const filterSummary = useMemo(() => {
        const parts: string[] = [];

        // Summarize active filters
        for (const col of filterableColumns) {
            const value = getFilterValue(state.filterModel, col.colId);
            if (value) {
                const displayValue = formatFilterDisplayValue(col, value);
                parts.push(`${col.headerName}: ${displayValue}`);
            }
        }

        // Summarize sort
        if (state.sortModel.length > 0) {
            const { colId, sort } = state.sortModel[0];
            const col = sortableColumns.find((c) => c.colId === colId);
            if (col) {
                parts.push(`Sort: ${col.headerName} ${sort === 'asc' ? '(A-Z)' : '(Z-A)'}`);
            }
        }

        return parts;
    }, [filterableColumns, sortableColumns, state.filterModel, state.sortModel]);

    if (filterableColumns.length === 0 && sortableColumns.length === 0) {
        return null;
    }

    return (
        <Box
            flexShrink={0}
            mb="12px"
            borderWidth="1px"
            borderColor={{ base: 'border', _dark: 'musa.dark.border' }}
            borderRadius="md"
            bg={{ base: 'white', _dark: 'musa.dark.500' }}>
            <Accordion.Root collapsible variant="plain" size="sm">
                <Accordion.Item value="filters">
                    <Accordion.ItemTrigger px="12px" py="8px" cursor="pointer">
                        <Flex flex="1" direction="column" gap="2px" alignItems="flex-start">
                            <Span fontSize="sm" fontWeight="500">
                                Filters &amp; Sort
                            </Span>
                            {filterSummary.length > 0 && (
                                <Text fontSize="xs" color="fg.muted" truncate maxW="100%">
                                    {filterSummary.join(' · ')}
                                </Text>
                            )}
                        </Flex>
                        <Accordion.ItemIndicator />
                    </Accordion.ItemTrigger>
                    <Accordion.ItemContent>
                        <Accordion.ItemBody px="12px" pb="12px" pt="0">
                            <Grid templateColumns="repeat(2, 1fr)" gap="8px">
                                {/* Primary search */}
                                {primarySearchCol && (
                                    <Box gridColumn="1 / -1">
                                        <Text fontSize="xs" color="fg.muted" mb="2px">
                                            Search {primarySearchCol.headerName}
                                        </Text>
                                        <InputGroup
                                            w="100%"
                                            startElement={
                                                <Icon>
                                                    <Search />
                                                </Icon>
                                            }>
                                            <DebouncedInput
                                                type="search"
                                                placeholder={`Search ${primarySearchCol.headerName}`}
                                                size="sm"
                                                value={getFilterValue(state.filterModel, primarySearchCol.colId)}
                                                onChange={(val) => handleDebouncedFilterChange(primarySearchCol.colId, val, 'filter')}
                                            />
                                        </InputGroup>
                                    </Box>
                                )}
                                {secondaryFilters.map(renderFilterControl)}
                                {sortableColumns.length > 0 && (
                                    <Box>
                                        <Text fontSize="xs" color="fg.muted" mb="2px">
                                            Sort by
                                        </Text>
                                        <NativeSelect.Root size="sm">
                                            <NativeSelect.Field value={currentSortValue} onChange={(e) => handleSortChange(e.target.value)}>
                                                <option value="">Default</option>
                                                {sortableColumns.map((col) => (
                                                    <optgroup key={col.colId} label={col.headerName}>
                                                        <option value={`${col.colId}:asc`}>{col.headerName} (A-Z)</option>
                                                        <option value={`${col.colId}:desc`}>{col.headerName} (Z-A)</option>
                                                    </optgroup>
                                                ))}
                                            </NativeSelect.Field>
                                            <NativeSelect.Indicator />
                                        </NativeSelect.Root>
                                    </Box>
                                )}
                            </Grid>
                        </Accordion.ItemBody>
                    </Accordion.ItemContent>
                </Accordion.Item>
            </Accordion.Root>
        </Box>
    );
}
